'use client';

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/components/providers/AuthProvider';
import { useProfileQuery } from '@/lib/hooks/queries/use-profile-query';
import { Zap, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function UserSettingsDialog({
  open,
  onOpenChange,
}: UserSettingsDialogProps) {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfileQuery();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onOpenChange(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md w-full p-0 bg-neutral-800 border-neutral-700 text-white'>
        {/* Header */}
        <DialogHeader className='flex flex-row items-center justify-between p-6 pb-4'>
          <DialogTitle className='text-xl font-semibold text-white'>
            Settings
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div className='px-6 pb-6 space-y-6'>
          <Separator className='bg-neutral-700' />
          {/* User Info Section */}
          <div className='space-y-4'>
            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>Username</Label>
              <span className='text-white text-lg'>
                {profile?.username ||
                  user?.user_metadata?.username ||
                  'Not set'}
              </span>
            </div>

            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>Email</Label>
              <span className='text-white text-lg'>
                {user?.email || 'Not available'}
              </span>
            </div>

            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Account Created
              </Label>
              <span className='text-white text-lg'>
                {user?.created_at
                  ? formatDate(user.created_at)
                  : 'Not available'}
              </span>
            </div>
          </div>

          {/* Divider */}
          <Separator className='bg-neutral-700' />

          {/* Subscription Info Section */}
          <div className='space-y-4'>
            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Available Credits
              </Label>
              <span className='text-white text-lg'>150</span>
            </div>

            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Subscription Plan
              </Label>
              <div className='flex items-center gap-2'>
                <span className='text-white'>Free</span>
                <Button
                  size='sm'
                  className={cn(
                    'bg-fuchsia-glow !pl-6 !pr-5',
                    'hover:from-fuchsia-700 hover:to-fuchsia-800',
                    'text-white font-medium transition-all duration-200',
                    'shadow-lg hover:shadow-fuchsia-500/25',
                    'border-0 rounded-full text-sm',
                  )}
                >
                  Upgrade
                  <Zap className='w-4 h-4 text-white fill-white' />
                </Button>
              </div>
            </div>

            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Expiration Date
              </Label>
              <span className='text-white text-lg'>N/A</span>
            </div>
          </div>

          {/* Divider */}
          <Separator className='bg-neutral-700' />

          {/* Danger Zone */}
          <div className='pt-2 space-y-4'>
            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Disconnect Account
              </Label>
              <Button
                variant='destructive'
                size='sm'
                onMouseDown={handleSignOut}
                className='bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2'
              >
                <LogOut className='w-4 h-4' />
                Disconnect
              </Button>
            </div>

            <div className='flex justify-between items-center py-2'>
              <Label className='text-neutral-400 text-base'>
                Delete Account
              </Label>
              <Button
                variant='destructive'
                size='sm'
                className='bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-full text-sm font-medium'
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
